// Testata della pagina ordine (redesign 2026-10-23). Ferma in alto mentre si
// scorre: chi è l'ordine resta a vista anche in fondo ai conti. Sopra CHI è
// (canale, numero, stato), sotto il contesto (data, fornitore, avvisi).
// I tasti non stanno più qui: ognuno vive nella tappa del percorso che fa
// avanzare (borderò e Zebra nell'Etichetta, «Manda al fornitore» e il numero
// ordine fornitore nel Fornitore). Resta solo la Conversazione col cliente,
// che non è un passo dell'ordine.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MarketplaceLogo } from "@/shared/ui/MarketplaceLogo";
import { fetchThreadsByOrder } from "@/features/messaggi/api";
import type { Order } from "../../types";
import { BTN, BTN_ICON } from "./stile";
import {
  Badge,
  CopyButton,
  formatArrivo,
  orderMarketplaceLogo,
  SupplierBadges,
} from "../OrderCard";
import { AddressIssuesBadge } from "./AddressIssuesBadge";
import { RigheRidotteBadge } from "./RigheRidotteBadge";
import { RefundBadge } from "./RefundBadge";

/** Bottone «Conversazione»: compare SOLO se esiste almeno un thread messaggi
 *  collegato all'ordine (match backend su external_order_id/oggetto).
 *  Deep-link a /messaggi?thread=ID&q=REF: apre la chat e filtra la lista sul
 *  ref (Davide 15/07). Fallimento fetch = nessun bottone, zero rumore.
 *
 *  ⚠️ Resta in testata e non scende in ActivitySection: quella sezione mostra
 *  le mail FORNITORE (thread /mail), questo è la conversazione col CLIENTE nei
 *  Messaggi — due link diversi verso due posti diversi. */
function ConversationButton({ reference }: { reference: string }) {
  const [thread, setThread] = useState<{ id: number; total: number } | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchThreadsByOrder(reference)
      .then((res) => {
        const first = res.items[0];
        if (!cancelled && first) {
          setThread({ id: first.id, total: res.total });
        }
      })
      .catch(() => {
        // niente conversazioni o mail non raggiungibile: il bottone non appare
      });
    return () => {
      cancelled = true;
    };
  }, [reference]);
  if (!thread) return null;
  return (
    <Link
      to={`/messaggi?thread=${thread.id}&q=${encodeURIComponent(reference)}`}
      className={BTN}
      title="Apri la conversazione di questo ordine nei Messaggi"
    >
      <svg className={BTN_ICON} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
      Conversazione{thread.total > 1 ? ` (${thread.total})` : ""}
    </Link>
  );
}

interface OrderHeaderProps {
  order: Order;
}

export function OrderHeader({ order }: OrderHeaderProps) {
  return (
    // Superfici INVERTITE rispetto al mock, di proposito: la pagina è già
    // ``bg-paper`` con card bianche — una testata color carta sparirebbe.
    <header className="sticky top-0 z-20 border-b border-line-2 bg-card/95 px-5 pb-3 pt-3.5 backdrop-blur max-md:px-3 max-md:pb-2.5 max-md:pt-1.5">
      <div className="mx-auto flex max-w-[1600px] flex-wrap items-center justify-between gap-x-6 gap-y-3">
        {/* Telefono: griglia [indietro | numero] e sotto, a tutta riga, il
            contesto — senza, la seconda riga restava rientrata di 56px. */}
        <div className="flex min-w-0 items-center gap-3 max-md:grid max-md:w-full max-md:grid-cols-[auto_minmax(0,1fr)] max-md:gap-x-2 max-md:gap-y-2">
          <Link
            to="/ordini"
            aria-label="Torna agli ordini"
            title="Torna agli ordini"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-line-2 bg-card text-ink transition-colors hover:bg-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 max-md:-ml-1 max-md:h-11 max-md:w-11 max-md:border-0 max-md:active:bg-paper-2"
          >
            <svg viewBox="0 0 24 24" className={BTN_ICON} aria-hidden="true">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </Link>
          <div className="min-w-0 max-md:contents">
            {/* Riga 1 — l'identità: si legge per prima, e basta da sola. */}
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 max-md:flex-nowrap max-md:gap-x-2">
              <MarketplaceLogo {...orderMarketplaceLogo(order)} size={22} />
              <h1 className="font-display text-[26px] font-semibold leading-none tracking-[-0.02em] text-ink max-md:min-w-0 max-md:truncate max-md:text-[21px]">
                {order.id}
              </h1>
              <CopyButton text={order.id} label="Copia ID ordine" />
              <span className="max-md:hidden">
                <Badge state={order.state} />
              </span>
            </div>
            {/* Riga 2 — il contesto, un tono sotto. */}
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1.5 max-md:col-span-2 max-md:mt-0">
              <span className="md:hidden">
                <Badge state={order.state} />
              </span>
              {order.createdAtPoleepo && formatArrivo(order.createdAtPoleepo) && (
                <time
                  dateTime={order.createdAtPoleepo}
                  data-testid="order-header-data"
                  className="text-[13px] font-medium tabular-nums text-ink"
                >
                  {formatArrivo(order.createdAtPoleepo)}
                </time>
              )}
              <SupplierBadges order={order} />
              <RefundBadge order={order} />
              <AddressIssuesBadge order={order} />
              <RigheRidotteBadge order={order} />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 empty:hidden max-md:w-full">
          <ConversationButton reference={order.id} />
        </div>
      </div>
    </header>
  );
}
